import { useCallback, useContext, useState } from 'react';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import type { ModalProps } from '@trinity/chat-components/components/Modal/Modal.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { ButtonOutlined } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { Select } from '@/components/presentation/Select';
import { MonologueDetailsEditSpeakerContext, TranscriptIdContext } from '@/containers/Transcript/context';
import { useEditSpeakerSelectionMutation } from '@/containers/Transcript/hooks';
import { cx } from '@trinity/chat-components';
import { ReassignSpeakerSelectionContext } from './context';
import styles from './style/EditSpeakers.module.css';

type Props = {
  children: React.ReactNode;
};

export const ReassignSpeakerSelectionContainer = (props: Props) => {
  const [state, dispatch] = useState<Value>(null);
  const transcriptId = useContext(TranscriptIdContext);

  const mutation = useEditSpeakerSelectionMutation({
    onSuccess: () => dispatch(null),
    transcriptId,
  });

  const value = {
    setValue: dispatch,
    mutation,
    value: state,
  };

  return (
    <ReassignSpeakerSelectionContext.Provider value={value}>
      {props.children}
      {!!state &&
        <ReassignSpeakerSelectionModal
          open={!!state}
          onClose={() => dispatch(null)} />}
    </ReassignSpeakerSelectionContext.Provider>
  );
};

ReassignSpeakerSelectionContainer.displayName = 'ReassignSpeakerSelection.Container';

type ReassignSpeakerSelectionModalProps = Pick<ModalProps, 'onClose' | 'open'>;

const ReassignSpeakerSelectionModal = (props: ReassignSpeakerSelectionModalProps) => {
  const md = useContext(MonologueDetailsEditSpeakerContext);
  const { mutation, value } = useContext(ReassignSpeakerSelectionContext);
  const [speakerIndex, setSpeakerIndex] = useState(value?.speakerIndex);

  const handleSubmit = useCallback(() => {
    mutation.mutate({
      speakerIndex,
      start: value?.start,
      end: value?.end,
    });
  }, [
    mutation,
    value?.end,
    value?.start,
    speakerIndex,
  ]);

  return (
    <Modal
      disableEscapeClose={mutation.isPending}
      disableOverlayClick={mutation.isPending}
      hideCloseIcon
      open={props.open}
      onClose={props.onClose}>
      <div className={cx(styles.root, styles.snippet)}>
        <Header>Edit Speaker</Header>
        <div className={styles.wrap}>
          <div className={styles.main}>
            <Select
              className={styles.input}
              onChange={e => setSpeakerIndex(e.target.value)}
              options={md?.options || []}
              placeholder="Select speaker"
              value={String(speakerIndex)} />
          </div>
          <div className={styles.actions}>
            <ButtonOutlined
              className={styles.btn}
              color="silver"
              disabled={mutation.isPending}
              onClick={props.onClose}
              size="small">
              Cancel
            </ButtonOutlined>

            <ButtonActivityIndicator
              className={styles.btn}
              disabled={speakerIndex === value.speakerIndex}
              loading={mutation.isPending}
              implicitDisable={false}
              indicatorSize={10}
              onClick={handleSubmit}
              size="small"
              variant="brick">
              Save
            </ButtonActivityIndicator>
          </div>
        </div>
      </div>
    </Modal>
  );
};

type Value = {
  end: number;
  speakerIndex: number;
  start: number;
};